import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { blogColumns } from "../../tables/blogColumns";
import { can } from "../../utils/can";
import { getImageUrl } from "../../utils/utils";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const toLiveSlug = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "");

const toFinalSlug = (text = "") => toLiveSlug(text).replace(/-+$/g, "");

const toDateTimeLocal = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const emptyForm = () => ({
  id: null,
  title: "",
  slug: "",
  shortDescription: "",
  metaTitle: "",
  metaKeywords: "",
  metaDescription: "",
  customScript: "",
  longDescription: "",
  image: null,
  publishDate: toDateTimeLocal(new Date()),
  publishStatus: "Draft",
  status: "Active",
});

export default function Blog() {
  const PAGE_SIZE = 10;
  const API_URL = (process.env.REACT_APP_BASE_URL_API || "").replace(/\/$/, "");
  const token = localStorage.getItem("adminToken");

  const [blogs, setBlogs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImages, setExistingImages] = useState({ list: "", detail: "" });

  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});

  const fetchBlogs = useCallback(async () => {
    try {
      setListLoading(true);
      const query = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortOrder,
      });

      const res = await fetch(`${API_URL}/api/admin/blog-list?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.success) {
        setBlogs(result.data || []);
        setTotalPages(result.totalPages || 1);
      } else {
        setBlogs([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Fetch blogs error:", err);
      setBlogs([]);
      setTotalPages(1);
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const validateForm = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = "Title is required.";
    if (!toFinalSlug(form.slug)) errors.slug = "Slug is required.";
    if (!form.shortDescription.trim()) {
      errors.shortDescription = "Short description is required.";
    }
    if (!form.longDescription.trim()) {
      errors.longDescription = "Long description is required.";
    }
    if (!form.id && !form.image) {
      errors.image = "Image is required.";
    }
    if (!form.publishDate) errors.publishDate = "Publish date is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setForm(emptyForm());
    setFormErrors({});
    setSlugEdited(false);
    setImagePreview("");
    setExistingImages({ list: "", detail: "" });
  };

  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: slugEdited ? prev.slug : toLiveSlug(value),
    }));
  };

  const handleSlugChange = (value) => {
    setSlugEdited(true);
    setForm((prev) => ({ ...prev, slug: toLiveSlug(value) }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const action = form.id ? "edit" : "create";
    if (can("blog", action) === false) {
      alert("You don't have permission.");
      return;
    }

    if (!validateForm()) return;

    setSubmitting(true);
    setMessage("");

    const formData = new FormData();
    formData.append("title", form.title.trim());
    formData.append("slug", toFinalSlug(form.slug));
    formData.append("shortDescription", form.shortDescription.trim());
    formData.append("metaTitle", form.metaTitle.trim());
    formData.append("metaKeywords", form.metaKeywords.trim());
    formData.append("metaDescription", form.metaDescription.trim());
    formData.append("customScript", form.customScript || "");
    formData.append("longDescription", form.longDescription);
    formData.append("publishDate", form.publishDate);
    formData.append("publishStatus", form.publishStatus);
    formData.append("status", form.status);
    if (form.image) {
      formData.append("image", form.image);
    }

    const url = form.id
      ? `${API_URL}/api/admin/blog-list/update/${form.id}`
      : `${API_URL}/api/admin/blog-create`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await res.json();

      if (result.success) {
        setMessage(`Blog ${form.id ? "updated" : "added"} successfully!`);
        resetForm();
        fetchBlogs();
      } else {
        alert(result.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("Submit blog error:", err);
      alert("Server error.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (blog) => {
    if (can("blog", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    setForm({
      id: blog._id,
      title: blog.title || "",
      slug: blog.slug || "",
      shortDescription: blog.shortDescription || "",
      metaTitle: blog.metaTitle || "",
      metaKeywords: blog.metaKeywords || "",
      metaDescription: blog.metaDescription || "",
      customScript: blog.customScript || "",
      longDescription: blog.longDescription || "",
      image: null,
      publishDate: toDateTimeLocal(blog.publishDate),
      publishStatus: blog.publishStatus || "Draft",
      status: blog.status || "Active",
    });
    setSlugEdited(true);
    setFormErrors({});
    setImagePreview("");
    setExistingImages({
      list: blog.imageList || "",
      detail: blog.imageDetail || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (can("blog", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/blog-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        fetchBlogs();
      } else {
        alert(result.message || "Failed to delete blog.");
      }
    } catch (err) {
      console.error("Delete blog error:", err);
      alert("Server error.");
    }
  };

  const columns = blogColumns(handleEdit, handleDelete, PAGE_SIZE, API_URL);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Blog" />

        <div className="right_col" role="main">
          <div className="x_panel">
            <div className="x_title">
              <h2>{form.id ? "Edit Blog" : "Blog Entry"}</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <form onSubmit={handleSubmit}>
                <div className="filtercont">
                  <div className="row">
                    <div className="col-12 col-sm-6 mb-3">
                      <label>Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                      />
                      {formErrors.title && (
                        <p className="text-danger">{formErrors.title}</p>
                      )}
                    </div>

                    <div className="col-12 col-sm-6 mb-3">
                      <label>Slug (unique)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.slug}
                        onChange={(e) => handleSlugChange(e.target.value)}
                      />
                      {formErrors.slug && (
                        <p className="text-danger">{formErrors.slug}</p>
                      )}
                    </div>

                    <div className="col-12 mb-3">
                      <label>Short Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={form.shortDescription}
                        onChange={(e) =>
                          setForm({ ...form, shortDescription: e.target.value })
                        }
                      />
                      {formErrors.shortDescription && (
                        <p className="text-danger">{formErrors.shortDescription}</p>
                      )}
                    </div>

                    <div className="col-12 col-sm-6 mb-3">
                      <label>Meta Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.metaTitle}
                        onChange={(e) =>
                          setForm({ ...form, metaTitle: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 col-sm-6 mb-3">
                      <label>Meta Keywords</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.metaKeywords}
                        onChange={(e) =>
                          setForm({ ...form, metaKeywords: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label>Meta Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={form.metaDescription}
                        onChange={(e) =>
                          setForm({ ...form, metaDescription: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label>Long Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={form.longDescription}
                        onChange={(_, editor) => {
                          setForm((prev) => ({
                            ...prev,
                            longDescription: editor.getData(),
                          }));
                        }}
                      />
                      {formErrors.longDescription && (
                        <p className="text-danger">{formErrors.longDescription}</p>
                      )}
                    </div>

                    <div className="col-12 col-sm-6 mb-3">
                      <label>Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/jpeg,image/png,image/webp,image/jpg"
                        onChange={handleImageChange}
                      />
                      <small className="text-muted d-block mt-1">
                        Recommended size: <strong>940 × 560</strong> px.
                        System auto-creates Detail (width 940) and List (width 320) keeping aspect ratio.
                      </small>
                      {formErrors.image && (
                        <p className="text-danger">{formErrors.image}</p>
                      )}
                      {(imagePreview || existingImages.list) && (
                        <div className="mt-2 d-flex gap-3 flex-wrap">
                          {imagePreview && (
                            <div>
                              <small>New preview</small>
                              <br />
                              <img
                                src={imagePreview ? getImageUrl(imagePreview) : ""}
                                alt="Preview"
                                style={{ maxWidth: 120, maxHeight: 90, objectFit: "contain" }}
                              />
                            </div>
                          )}
                          {existingImages.list && !imagePreview && (
                            <div>
                              <small>List (320px)</small>
                              <br />
                              <img
                                src={getImageUrl(existingImages.list)}
                                alt="List"
                                style={{ maxWidth: 120, maxHeight: 90, objectFit: "contain" }}
                              />
                            </div>
                          )}
                          {existingImages.detail && !imagePreview && (
                            <div>
                              <small>Detail (940px)</small>
                              <br />
                              <img
                                src={getImageUrl(existingImages.detail)}
                                alt="Detail"
                                style={{ maxWidth: 160, maxHeight: 120, objectFit: "contain" }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-sm-3 mb-3">
                      <label>Publish Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={form.publishDate}
                        onChange={(e) =>
                          setForm({ ...form, publishDate: e.target.value })
                        }
                      />
                      {formErrors.publishDate && (
                        <p className="text-danger">{formErrors.publishDate}</p>
                      )}
                    </div>

                    <div className="col-12 col-sm-3 mb-3">
                      <label>Publish Status</label>
                      <select
                        className="form-select"
                        value={form.publishStatus}
                        onChange={(e) =>
                          setForm({ ...form, publishStatus: e.target.value })
                        }
                      >
                        <option value="Draft">Draft</option>
                        <option value="Publish">Publish</option>
                      </select>
                    </div>

                    <div className="col-12 col-sm-3 mb-3">
                      <label>Status</label>
                      <select
                        className="form-select"
                        value={form.status}
                        onChange={(e) =>
                          setForm({ ...form, status: e.target.value })
                        }
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>

                    <div className="col-12 mb-3">
                      <label>Custom Scripts (GTM, Schema, Analytics)</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        placeholder="<script type=&quot;application/ld+json&quot;>...</script> or <script>...</script>"
                        value={form.customScript}
                        onChange={(e) =>
                          setForm({ ...form, customScript: e.target.value })
                        }
                      />
                      <small className="text-muted">
                        Paste custom Schema.org LD+JSON, tracking pixels, or head scripts for this blog post.
                      </small>
                    </div>

                    <div className="col-12 text-right">
                      <button
                        type="submit"
                        className="btn sitebtn me-2"
                        disabled={submitting}
                      >
                        {submitting ? "Submitting..." : "Submit"}
                      </button>
                      {form.id && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={resetForm}
                        >
                          Cancel Edit
                        </button>
                      )}
                      {message && (
                        <div className="text-success mt-2">{message}</div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="x_panel">
            <div className="x_title">
              <h2>Blog List</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <AdminDataTable
                columns={columns}
                data={blogs}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
